import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Package, Check, Plus, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import config from '../../config/config';
import { BaskiGorselComponent } from './BaskiGorselLazyImage';
import BaskiGorselGalleryModal from './BaskiGorselGalleryModal';
import { normalizeBaskiGorselUrls, getFirstBaskiGorselUrl } from '../../utils/baskiGorselUtils';
import {
  isParcaBaskiProsesName,
  composeBedenDagilimi,
  parseBedenDagilimi,
  sumBedenAdet,
  commonBedenSuggestions,
  cloneDefaultParcaBaskiBedenRows
} from '../../utils/parcaBaskiBedenUtils';

const SiparisOlusturModal = ({ isOpen, onClose, onSave, editData = null, selectedDesen: propSelectedDesen = null, hideDesenSelection = false }) => {
  // Ana state - Basit ve temiz
  const [siparis, setSiparis] = useState({
    musteri_id: '',
    musteri_adi: '',
    isletme_id: '',
    isletme_adi: '',
    stok_karti_id: '',
    stok_karti_adi: '',
    proses_id: '',
    doviz_id: '',
    proses_fiyati: '',
    siparis_tarihi: new Date().toISOString().split('T')[0], // Bugünün tarihi
    toplam_metre: 0, // Toplam metre (varyantlardan hesaplanacak)
    toplam_fiyat: 0, // Toplam fiyat (varyantlardan hesaplanacak)
    doviz_kuru: 1, // Döviz kuru (varsayılan 1)
    notlar: '', // Sipariş notu (tüm kullanıcılar düzenleyebilir)
    isletme_notu: '', // İşletme notu (hem admin hem işletme düzenleyebilir)
    varyantlar: []
  });

  // UI state'leri
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDropdowns, setShowDropdowns] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [galleryState, setGalleryState] = useState({
    open: false,
    urls: [],
    title: '',
    index: 0
  });

  // Data state'leri
  const [desenler, setDesenler] = useState([]);
  const [desenSearch, setDesenSearch] = useState('');
  const [musteriler, setMusteriler] = useState([]);
  const [isletmeler, setIsletmeler] = useState([]);
  const [stokKartlari, setStokKartlari] = useState([]);
  const [prosesler, setProsesler] = useState([]);
  const [paraBirimleri, setParaBirimleri] = useState([]);

  // Seçili desen ve varyantlar
  const [selectedDesen, setSelectedDesen] = useState(null);
  const [varyantlar, setVaryantlar] = useState([]);

  // Modal açıldığında tüm verileri yükle
  useEffect(() => {
    if (isOpen) {
      loadAllData();
      // Eğer prop'tan desen geliyorsa, onu seç
      if (propSelectedDesen) {
        handleDesenSelect(propSelectedDesen);
      }
    }
  }, [isOpen, propSelectedDesen]);

  // Stok kartı değiştiğinde varyantları güncelle
  useEffect(() => {
    if (siparis.stok_karti_id && siparis.varyantlar.length > 0) {
      setSiparis(prev => ({
        ...prev,
        varyantlar: prev.varyantlar.map(varyant => ({
          ...varyant,
          stok_kart_id: prev.stok_karti_id
        }))
      }));
    }
  }, [siparis.stok_karti_id, siparis.varyantlar.length]);

  // Varyantlar değiştiğinde toplam metre ve fiyatı hesapla
  useEffect(() => {
    const toplamMetre = siparis.varyantlar.reduce((total, varyant) => total + (parseFloat(varyant.metre) || 0), 0);
    const birimFiyat = parseFloat(siparis.proses_fiyati) || 0;
    const toplamFiyat = toplamMetre * birimFiyat;

    setSiparis(prev => ({
      ...prev,
      toplam_metre: toplamMetre,
      toplam_fiyat: toplamFiyat
    }));
  }, [siparis.varyantlar, siparis.proses_fiyati]);

  const selectedProses = useMemo(
    () => prosesler.find((p) => String(p.id) === String(siparis.proses_id)),
    [prosesler, siparis.proses_id]
  );
  const isParcaBaski = isParcaBaskiProsesName(selectedProses?.processName);
  const prevIsParcaBaskiRef = useRef(false);

  const getVaryantBedenRows = (selectedVaryant) => {
    if (selectedVaryant?.bedenRows?.length) return selectedVaryant.bedenRows;
    if (selectedVaryant?.beden_dagilimi) {
      const parsed = parseBedenDagilimi(selectedVaryant.beden_dagilimi);
      if (parsed.length) return parsed;
    }
    return cloneDefaultParcaBaskiBedenRows();
  };

  const syncMetreFromBedenRows = (varyant) => ({
    ...varyant,
    metre: sumBedenAdet(getVaryantBedenRows(varyant))
  });

  // Parça baskı: tüm varyantlara varsayılan beden satırları
  useEffect(() => {
    if (!isOpen || !isParcaBaski || varyantlar.length === 0) return;

    setSiparis((prev) => {
      const existingMap = new Map(prev.varyantlar.map((v) => [v.desen_varyant_id, v]));

      if (editData) {
        const nextVaryantlar = prev.varyantlar.map((v) => syncMetreFromBedenRows({
          ...v,
          bedenRows: getVaryantBedenRows(v)
        }));
        const unchanged = nextVaryantlar.every((v, i) => v.metre === prev.varyantlar[i]?.metre
          && v.bedenRows?.length === prev.varyantlar[i]?.bedenRows?.length);
        return unchanged ? prev : { ...prev, varyantlar: nextVaryantlar };
      }

      const nextVaryantlar = varyantlar.map((v) => {
        const existing = existingMap.get(v.id);
        const bedenRows = existing?.bedenRows?.length
          ? existing.bedenRows
          : cloneDefaultParcaBaskiBedenRows();
        return syncMetreFromBedenRows({
          ...(existing || {}),
          desen_varyant_id: v.id,
          siparis_varyant_adi: v.varyant_adi || 'Ana',
          siparis_varyant_kodu: v.varyant_no || '1',
          stok_kart_id: existing?.stok_kart_id || prev.stok_karti_id,
          bedenRows
        });
      });

      const unchanged = nextVaryantlar.length === prev.varyantlar.length
        && nextVaryantlar.every((v) => existingMap.has(v.desen_varyant_id) && existingMap.get(v.desen_varyant_id)?.bedenRows?.length);
      return unchanged ? prev : { ...prev, varyantlar: nextVaryantlar };
    });
  }, [isParcaBaski, isOpen, varyantlar, editData]);

  // Parça baskı dışına çıkınca beden state temizle
  useEffect(() => {
    if (!isOpen) return;
    if (isParcaBaski) {
      prevIsParcaBaskiRef.current = true;
      return;
    }
    if (!prevIsParcaBaskiRef.current) return;

    prevIsParcaBaskiRef.current = false;
    setSiparis((prev) => ({
      ...prev,
      varyantlar: prev.varyantlar
        .filter((v) => parseFloat(v.metre) > 0)
        .map(({ bedenRows, beden_dagilimi, ...rest }) => rest)
    }));
  }, [isParcaBaski, isOpen]);


  // Edit mode ise verileri yükle
  useEffect(() => {
    if (editData && isOpen) {
      const loadEditData = async () => {
        // Önce tüm verileri yükle
        await loadAllData();
        
        // Sonra sipariş verilerini set et
        setSiparis({
          musteri_id: editData.musteri_id || '',
          musteri_adi: editData.musteri_adi || '',
          isletme_id: editData.isletme_id || '',
          isletme_adi: editData.isletme_adi || '',
          stok_karti_id: editData.stok_karti_id || '',
          stok_karti_adi: editData.stok_karti_adi || '',
          proses_id: editData.proses_id || '',
          doviz_id: editData.doviz_id || '',
          proses_fiyati: editData.proses_fiyati || '',
          siparis_tarihi: editData.siparis_tarihi || new Date().toISOString().split('T')[0],
          toplam_metre: editData.toplam_metre || 0,
          toplam_fiyat: editData.toplam_fiyat || 0,
          doviz_kuru: editData.doviz_kuru || 1,
          notlar: editData.notlar || '',
          isletme_notu: editData.isletme_notu || '',
          // Sadece düzenlenen varyantı göster (editData zaten sadece o varyantı içeriyor)
          varyantlar: (editData.varyantlar || []).map(v => ({
            desen_varyant_id: v.desen_varyant_id || v.desen_id || v.id,
            siparis_varyant_adi: v.siparis_varyant_adi || v.varyant_adi || 'Ana',
            siparis_varyant_kodu: v.siparis_varyant_kodu || v.varyant_no || '1',
            metre: v.metre || 0,
            stok_kart_id: v.stok_kart_id || editData.stok_kart_id || editData.stok_karti_id,
            desen: v.desen,
            bedenRows: parseBedenDagilimi(v.beden_dagilimi || '')
          }))
        });
        
              // İşletme seçiliyse prosesleri yükle
              if (editData.isletme_id) {
                try {
                  const response = await api.get(`/api/baski/isletmeler/${editData.isletme_id}/prosesler`);
                  if (response.data.success) {
                    setProsesler(response.data.data.prosesler || []);
                  }
                } catch (error) {
                  console.error('❌ Edit modunda prosesler yüklenemedi:', error);
                  if (error.response?.status !== 429) {
                    toast.error('Prosesler yüklenemedi');
                  }
                }
              }

        // Edit modunda desen bilgisini yükle - sadece düzenlenen varyant için
        if (editData.varyantlar && editData.varyantlar.length > 0) {
          // editData zaten sadece düzenlenen varyantı içeriyor
          const targetVariant = editData.varyantlar[0];
          
          if (targetVariant && targetVariant.desen) {
            // Desen bilgisini işle
            const processedDesen = { ...targetVariant.desen };
            
            // gorsel_urls'ı işle
            if (processedDesen.gorsel_urls) {
              let gorselUrls = processedDesen.gorsel_urls;
              
              // String ise JSON parse et
              if (typeof gorselUrls === 'string') {
                try {
                  gorselUrls = JSON.parse(gorselUrls);
                } catch (e) {
                  gorselUrls = [gorselUrls];
                }
              }
              
              // Array ise URL'leri tam hale getir
              if (Array.isArray(gorselUrls)) {
                gorselUrls = gorselUrls.map(url => {
                  const cleanUrl = String(url).replace(/^["']+|["']+$/g, '');
                  return cleanUrl.startsWith('http') ? cleanUrl : `${config.backendUrl}${cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`}`;
                });
              }
              
              processedDesen.gorsel_urls = gorselUrls;
            }
            
            setSelectedDesen(processedDesen);
            
            // Sadece düzenlenen varyantı yükle
            const processedVaryant = {
              id: targetVariant.desen_varyant_id || targetVariant.desen_id || targetVariant.id,
              varyant_adi: targetVariant.siparis_varyant_adi || targetVariant.varyant_adi || 'Ana',
              varyant_no: targetVariant.siparis_varyant_kodu || targetVariant.varyant_no || '1',
              gorsel_urls: processedDesen.gorsel_urls || [],
              birim_fiyat: 0
            };
            
            setVaryantlar([processedVaryant]);
          }
        }
      };
      
      loadEditData();
    }
  }, [editData, isOpen]);

  // Tüm verileri yükle
  const loadAllData = async () => {
    setLoading(true);
    try {
      const desenlerRes = await api.get('/api/baski/desenler');
      if (desenlerRes.data?.success) {
        // Desenlerin gorsel_urls'ını işle
        const processedDesenler = (desenlerRes.data.data || []).map(desen => {
          let processedD = { ...desen };
          
          // gorsel_urls'ı işle
          if (processedD.gorsel_urls) {
            let gorselUrls = processedD.gorsel_urls;
            
            // String ise JSON parse et
            if (typeof gorselUrls === 'string') {
              try {
                gorselUrls = JSON.parse(gorselUrls);
              } catch (e) {
                gorselUrls = [gorselUrls];
              }
            }
            
            // Array ise URL'leri tam hale getir
            if (Array.isArray(gorselUrls)) {
              gorselUrls = gorselUrls.map(url => {
                const cleanUrl = String(url).replace(/^["']+|["']+$/g, '');
                return cleanUrl.startsWith('http') ? cleanUrl : cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
              });
            }
            
            processedD.gorsel_urls = gorselUrls;
          }
          
          return processedD;
        });
        
        setDesenler(processedDesenler);
      }

      const musterilerRes = await api.get('/api/baski/cari-hesaplar');
      if (musterilerRes.data?.success) {
        setMusteriler(musterilerRes.data.data || []);
      }

      const isletmelerRes = await api.get('/api/baski/dijital-baski-isletmeler');
      if (isletmelerRes.data?.success) {
        setIsletmeler(isletmelerRes.data.data || []);
      }

      const stokRes = await api.get('/api/baski/stok-kartlari');
      if (stokRes.data?.success) {
        setStokKartlari(stokRes.data.data || []);
      }

      const dovizRes = await api.get('/api/baski/para-birimleri');
      if (dovizRes.data?.success) {
        setParaBirimleri(dovizRes.data.data || []);
      }

    } catch (error) {
      console.error('API hatası:', error);
      toast.error(`Veriler yüklenemedi: ${error.response?.status || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // İşletme değiştiğinde prosesleri yükle
  const handleIsletmeChange = async (isletmeId) => {
    try {
      const res = await api.get(`/api/baski/isletmeler/${isletmeId}/prosesler`);
      if (res.data?.success) {
        const list = res.data?.data?.prosesler || [];
        setProsesler(list);
        setSiparis(prev => ({ ...prev, isletme_id: isletmeId, proses_id: '', proses_fiyati: '' }));
      }
    } catch (error) {
      console.error('Prosesler yüklenemedi:', error);
      toast.error('Prosesler yüklenemedi');
    }
  };

  // Proses değiştiğinde fiyatı al
  const handleProsesChange = (proses) => {
    if (proses) {
      setSiparis(prev => ({
        ...prev,
        proses_id: proses.id,
        proses_fiyati: proses.processPrice || '',
        doviz_id: proses.currencyId || ''
      }));
    }
  };

  // Desen seçildiğinde varyantları yükle
  const handleDesenSelect = async (desen) => {
    // Desen'in gorsel_urls'ını işle
    let processedDesen = { ...desen };
    
    if (processedDesen.gorsel_urls) {
      let gorselUrls = processedDesen.gorsel_urls;
      
      // String ise JSON parse et
      if (typeof gorselUrls === 'string') {
        try {
          gorselUrls = JSON.parse(gorselUrls);
        } catch (e) {
          gorselUrls = [gorselUrls];
        }
      }
      
      // Array ise URL'leri tam hale getir
      if (Array.isArray(gorselUrls)) {
        gorselUrls = gorselUrls.map(url => {
          const cleanUrl = String(url).replace(/^["']+|["']+$/g, '');
          return cleanUrl.startsWith('http') ? cleanUrl : cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
        });
      }
      
      processedDesen.gorsel_urls = gorselUrls;
    }
    
    setSelectedDesen(processedDesen);
    setSiparis(prev => ({ ...prev, varyantlar: [] }));

    if (desen?.musteri_id) {
      const musteriIdString = String(desen.musteri_id);
      setSiparis(prev => ({
        ...prev,
        musteri_id: musteriIdString,
        musteri_adi: desen.musteri_adi || prev.musteri_adi
      }));

      setMusteriler(prev => {
        if (!Array.isArray(prev)) {
          return prev;
        }

        const exists = prev.some(m => String(m.id) === musteriIdString);
        if (exists) {
          return prev;
        }

        return [
          ...prev,
          {
            id: desen.musteri_id,
            name: desen.musteri_adi || `Cari #${desen.musteri_id}`
          }
        ];
      });
    }

    const rawStokKartId =
      desen?.stok_kart_id ??
      desen?.stokKartId ??
      (desen?.stokKart ? desen.stokKart.id : null);

    if (rawStokKartId) {
      const stokId = Number(rawStokKartId);
      setSiparis(prev => ({
        ...prev,
        stok_karti_id: stokId,
        stok_karti_adi: desen.stok_kart_adi || prev.stok_karti_adi
      }));

      setStokKartlari(prev => {
        if (!Array.isArray(prev)) {
          return prev;
        }

        const exists = prev.some(s => Number(s.id) === stokId);
        if (exists) {
          return prev;
        }

        return [
          ...prev,
          {
            id: desen.stok_kart_id,
            name: desen.stok_kart_adi || `Stok #${desen.stok_kart_id}`
          }
        ];
      });
    }

    try {
      const res = await api.get(`/api/baski/desenler/${desen.id}/varyantlar`);
      if (res.data?.success) {
        // Varyantların gorsel_urls'ını işle
        const processedVaryantlar = (res.data.data || []).map(varyant => {
          let processedV = { ...varyant };
          
          // gorsel_urls'ı işle
          if (processedV.gorsel_urls) {
            let gorselUrls = processedV.gorsel_urls;
            
            // String ise JSON parse et
            if (typeof gorselUrls === 'string') {
              try {
                gorselUrls = JSON.parse(gorselUrls);
              } catch (e) {
                gorselUrls = [gorselUrls];
              }
            }
            
            // Array ise URL'leri tam hale getir
            if (Array.isArray(gorselUrls)) {
              gorselUrls = gorselUrls.map(url => {
                const cleanUrl = String(url).replace(/^["']+|["']+$/g, '');
                return cleanUrl.startsWith('http') ? cleanUrl : cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
              });
            }
            
            processedV.gorsel_urls = gorselUrls;
          }
          
          // Desen içindeki gorsel_urls'ı da kontrol et
          if (processedV.desen?.gorsel_urls) {
            let desenGorselUrls = processedV.desen.gorsel_urls;
            
            if (typeof desenGorselUrls === 'string') {
              try {
                desenGorselUrls = JSON.parse(desenGorselUrls);
              } catch (e) {
                desenGorselUrls = [desenGorselUrls];
              }
            }
            
            if (Array.isArray(desenGorselUrls)) {
              desenGorselUrls = desenGorselUrls.map(url => {
                const cleanUrl = String(url).replace(/^["']+|["']+$/g, '');
                return cleanUrl.startsWith('http') ? cleanUrl : cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
              });
            }
            
            // Eğer varyantın kendi gorsel_urls'ı yoksa, desen'den al
            if (!processedV.gorsel_urls || processedV.gorsel_urls.length === 0) {
              processedV.gorsel_urls = desenGorselUrls;
            }
            
            processedV.desen.gorsel_urls = desenGorselUrls;
          }
          
          return processedV;
        });
        
        setVaryantlar(processedVaryantlar);
      }
    } catch (error) {
      console.error('Varyantlar yüklenemedi:', error);
      toast.error('Varyantlar yüklenemedi');
    }
  };

  // Varyant metre değişikliği
  const handleVaryantMetreChange = (varyantId, metre) => {
    const numMetre = metre === '' ? 0 : (parseFloat(metre) || 0);

    setSiparis(prev => {
      if (numMetre > 0) {
        // Metre > 0 ise ekle/güncelle
        const existingIndex = prev.varyantlar.findIndex(v => v.desen_varyant_id === varyantId);

        if (existingIndex >= 0) {
          // Güncelle
          const updated = [...prev.varyantlar];
          updated[existingIndex] = { ...updated[existingIndex], metre: numMetre };
          return { ...prev, varyantlar: updated };
        } else {
          // Yeni ekle
          const varyant = varyantlar.find(v => v.id === varyantId);
          if (varyant) {
            return {
              ...prev,
              varyantlar: [...prev.varyantlar, {
                desen_varyant_id: varyant.id,
                siparis_varyant_adi: varyant.varyant_adi || 'Ana',
                siparis_varyant_kodu: varyant.varyant_no || '1',
                metre: numMetre,
                kg: 0,
                top_sayisi: 0,
                stok_kart_id: prev.stok_karti_id,
                bedenRows: []
              }]
            };
          }
        }
      } else {
        // Metre = 0 ise çıkar
        return {
          ...prev,
          varyantlar: prev.varyantlar.filter(v => v.desen_varyant_id !== varyantId)
        };
      }
      return prev;
    });
  };

  const updateVaryantBedenRows = (varyantId, updater) => {
    setSiparis(prev => {
      const existingIndex = prev.varyantlar.findIndex(v => v.desen_varyant_id === varyantId);

      if (existingIndex >= 0) {
        const nextVaryantlar = prev.varyantlar.map(v => {
          if (v.desen_varyant_id !== varyantId) return v;
          const currentRows = Array.isArray(v.bedenRows) && v.bedenRows.length > 0
            ? v.bedenRows
            : cloneDefaultParcaBaskiBedenRows();
          const nextRows = typeof updater === 'function' ? updater(currentRows) : updater;
          const metre = sumBedenAdet(nextRows);
          return { ...v, bedenRows: nextRows, metre };
        });
        return { ...prev, varyantlar: nextVaryantlar };
      }

      const desenVaryant = varyantlar.find(v => v.id === varyantId);
      if (!desenVaryant) return prev;

      const currentRows = cloneDefaultParcaBaskiBedenRows();
      const nextRows = typeof updater === 'function' ? updater(currentRows) : updater;
      const metre = sumBedenAdet(nextRows);

      return {
        ...prev,
        varyantlar: [...prev.varyantlar, {
          desen_varyant_id: desenVaryant.id,
          siparis_varyant_adi: desenVaryant.varyant_adi || 'Ana',
          siparis_varyant_kodu: desenVaryant.varyant_no || '1',
          metre,
          stok_kart_id: prev.stok_karti_id,
          bedenRows: nextRows
        }]
      };
    });
  };

  const handleBedenRowChange = (varyantId, rowIndex, field, value) => {
    updateVaryantBedenRows(varyantId, (rows) => {
      const next = [...rows];
      if (!next[rowIndex]) {
        next[rowIndex] = { bedenAdi: '', adet: '' };
      }
      next[rowIndex] = { ...next[rowIndex], [field]: value };
      return next;
    });
  };

  const handleAddBedenRow = (varyantId) => {
    updateVaryantBedenRows(varyantId, (rows) => [...rows, { bedenAdi: '', adet: '' }]);
  };

  const handleRemoveBedenRow = (varyantId, rowIndex) => {
    updateVaryantBedenRows(varyantId, (rows) => rows.filter((_, idx) => idx !== rowIndex));
  };

  const handleQuickAddBeden = (varyantId, bedenAdi) => {
    updateVaryantBedenRows(varyantId, (rows) => {
      const trimmed = String(bedenAdi || '').trim();
      if (!trimmed) return rows;

      const existingIndex = rows.findIndex(
        (row) => String(row.bedenAdi || '').trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (existingIndex >= 0) {
        const next = [...rows];
        const currentAdet = parseInt(next[existingIndex].adet, 10) || 0;
        next[existingIndex] = { ...next[existingIndex], adet: String(currentAdet + 1) };
        return next;
      }
      return [...rows, { bedenAdi: trimmed, adet: '' }];
    });
  };

  const getVaryantDisplayName = (selectedVaryant) => {
    const fromList = varyantlar.find(v => v.id === selectedVaryant.desen_varyant_id);
    return selectedVaryant.siparis_varyant_adi || fromList?.varyant_adi || 'Varyant';
  };

  // Dropdown göster/gizle
  const toggleDropdown = (name) => {
    setShowDropdowns(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Dropdown seçimi
  const handleSelect = async (name, item) => {
    const updates = { [name]: item.id };
    
    // İsim alanlarını da güncelle
    if (name === 'musteri_id') {
      updates.musteri_adi = item.name || item.fullName || item.companyName;
    } else if (name === 'isletme_id') {
      updates.isletme_adi = item.name || item.fullName || item.companyName;
      console.log('İşletme seçildi:', item);
      console.log('İşletme adı:', updates.isletme_adi);
      
      // İşletme seçildiğinde prosesleri yükle (debounced)
      if (item.id && item.id !== siparis.isletme_id) {
        try {
          const response = await api.get(`/api/baski/isletmeler/${item.id}/prosesler`);
          if (response.data.success) {
            setProsesler(response.data.data.prosesler || []);
          }
        } catch (error) {
          console.error('❌ Prosesler yüklenemedi:', error);
          if (error.response?.status !== 429) { // Rate limit hatası değilse toast göster
            toast.error('Prosesler yüklenemedi');
          }
        }
      }
    } else if (name === 'stok_karti_id') {
      updates.stok_karti_adi = item.name || item.stockName;
      
      // Edit modunda: stok kartı değişikliği sadece o varyanta uygulanmalı
      if (editData && siparis.varyantlar.length > 0) {
        // Sadece ilk varyantı güncelle (edit modunda tek varyant var)
        setSiparis(prev => ({
          ...prev,
          ...updates,
          varyantlar: prev.varyantlar.map((v, index) => 
            index === 0 
              ? { ...v, stok_kart_id: item.id }
              : v
          )
        }));
        setShowDropdowns({});
        return; // Erken çık, aşağıdaki setSiparis'i çalıştırma
      }
    } else if (name === 'proses_id') {
      // Proses seçildiğinde fiyat ve para birimini otomatik doldur
      updates.proses_fiyati = item.processPrice || '';
      updates.doviz_id = item.currencyId || '';
    }
    
    setSiparis(prev => ({ ...prev, ...updates }));
    setShowDropdowns({});
  };

  // Modal dışına tıklandığında kapat
  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Dropdown container click
  const handleDropdownContainerClick = (e) => {
    e.stopPropagation();
  };

  // Resim büyütme — desenin tüm görsellerini galeride aç
  const handleImageClick = (imageUrlOrUrls, title, startIndex = 0) => {
    const urls = normalizeBaskiGorselUrls(
      Array.isArray(imageUrlOrUrls) || (typeof imageUrlOrUrls === 'string' && imageUrlOrUrls.trim().startsWith('['))
        ? imageUrlOrUrls
        : imageUrlOrUrls
          ? [imageUrlOrUrls]
          : []
    );
    if (urls.length === 0) return;
    setGalleryState({
      open: true,
      urls,
      title: title || 'Desen görselleri',
      index: startIndex
    });
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
    setGalleryState({ open: false, urls: [], title: '', index: 0 });
  };

  // Sipariş kaydet
  const handleSave = async () => {
    // Validasyon
    if (!siparis.musteri_id) {
      toast.error('Müşteri seçin');
      return;
    }
    if (!siparis.isletme_id) {
      toast.error('İşletme seçin');
      return;
    }
    if (!siparis.stok_karti_id) {
      toast.error('Stok kartı seçin');
      return;
    }
    if (!siparis.proses_id) {
      toast.error('Proses seçin');
      return;
    }
    if (!selectedDesen) {
      toast.error('Desen seçin');
      return;
    }

    const varyantlarToSave = isParcaBaski
      ? siparis.varyantlar.filter((v) => sumBedenAdet(v.bedenRows) > 0)
      : siparis.varyantlar.filter((v) => parseFloat(v.metre) > 0);

    if (varyantlarToSave.length === 0) {
      toast.error(isParcaBaski ? 'En az bir varyant için beden adedi girin' : 'En az bir varyant için metre girin');
      return;
    }

    setSaving(true);
    try {
        // Yeni API yapısı: her varyant için ayrı desen_id
        // Edit modunda: stok_kart_id varyanttan alınmalı (bağımsız olması için)
        const firstVariant = siparis.varyantlar[0];
        const stokKartId = editData && firstVariant?.stok_kart_id 
          ? firstVariant.stok_kart_id 
          : siparis.stok_karti_id;
        const stokKartAdi = editData && firstVariant?.stok_kart_id
          ? stokKartlari.find(s => s.id === firstVariant.stok_kart_id)?.name || siparis.stok_karti_adi
          : siparis.stok_karti_adi;
        
        const requestData = {
          musteri_id: siparis.musteri_id,
          musteri_adi: siparis.musteri_adi,
          siparis_tarihi: siparis.siparis_tarihi,
          isletme_id: siparis.isletme_id,
          stok_kart_id: stokKartId,
          stok_karti_adi: stokKartAdi,
          proses_id: siparis.proses_id,
          proses_fiyati: siparis.proses_fiyati,
          doviz_id: siparis.doviz_id,
          doviz_kuru: siparis.doviz_kuru,
          notlar: siparis.notlar,
          isletme_notu: siparis.isletme_notu,
          varyantlar: varyantlarToSave.map(v => {
            const bedenDagilimi = isParcaBaski
              ? (composeBedenDagilimi(v.bedenRows) || null)
              : null;
            const metre = isParcaBaski ? sumBedenAdet(v.bedenRows) : v.metre;
            return {
              desen_id: v.desen_varyant_id,
              siparis_varyant_adi: v.siparis_varyant_adi,
              siparis_varyant_kodu: v.siparis_varyant_kodu,
              metre,
              beden_dagilimi: bedenDagilimi,
              stok_kart_id: editData ? (v.stok_kart_id || stokKartId) : stokKartId
            };
          })
        };

      const res = editData
        ? await api.put(`/api/baski/siparisler/${editData.id}`, requestData)
        : await api.post('/api/baski/siparisler', requestData);

      if (res.data?.success) {
        toast.success(editData ? 'Sipariş güncellendi' : 'Sipariş oluşturuldu');
        onClose();
        if (typeof onSave === 'function') {
          try { onSave(res.data); } catch (_) {}
        }
      } else {
        toast.error(res.data?.message || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Sipariş kaydedilemedi:', error);
      toast.error('Sipariş kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // Body scroll locking
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const renderVaryantBedenBlock = (varyantId, bedenRows) => {
    if (!isParcaBaski) return null;
    const previewText = composeBedenDagilimi(bedenRows);

    return (
      <div className="border-t border-orange-200 dark:border-orange-800/50 bg-orange-50/80 dark:bg-orange-950/20 p-1.5 space-y-1.5">
        <p className="text-[10px] font-medium text-orange-900 dark:text-orange-100">Beden (opsiyonel)</p>

        <div className="flex flex-wrap gap-0.5">
          {commonBedenSuggestions.map((suggestion) => (
            <button
              key={`${varyantId}-${suggestion}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleQuickAddBeden(varyantId, suggestion);
              }}
              className="px-1 py-0.5 text-[9px] rounded border border-orange-300/80 dark:border-orange-700 text-orange-900 dark:text-orange-100 bg-white/90 dark:bg-gray-900 hover:bg-orange-100 dark:hover:bg-orange-900/40"
            >
              + {suggestion}
            </button>
          ))}
        </div>

        <div className="space-y-1">
          {bedenRows.map((row, rowIndex) => (
            <div key={`${varyantId}-beden-${rowIndex}`} className="flex items-center gap-1">
              <input
                type="text"
                placeholder="Beden"
                value={row.bedenAdi || ''}
                onChange={(e) => handleBedenRowChange(varyantId, rowIndex, 'bedenAdi', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 min-w-0 px-1.5 py-1 text-[10px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <input
                type="number"
                placeholder="Adet"
                min="0"
                step="1"
                value={row.adet ?? ''}
                onChange={(e) => handleBedenRowChange(varyantId, rowIndex, 'adet', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-12 px-1 py-1 text-[10px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveBedenRow(varyantId, rowIndex);
                }}
                className="p-0.5 text-red-500 hover:text-red-700 dark:hover:text-red-400"
                title="Satırı sil"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleAddBedenRow(varyantId);
          }}
          className="inline-flex items-center gap-0.5 text-[10px] text-orange-800 dark:text-orange-200 hover:underline"
        >
          <Plus className="h-2.5 w-2.5" />
          Beden ekle
        </button>

        {previewText && (
          <p className="text-[9px] text-gray-600 dark:text-gray-400 break-words leading-tight">
            {previewText}
          </p>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleModalClick}>
      <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl shadow-2xl max-w-4xl w-full h-auto max-h-[95vh] flex flex-col relative">
          {/* Header */}
          <div className="flex justify-between items-center p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {editData ? 'Sipariş Düzenle' : 'Yeni Sipariş'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-4 space-y-3 overflow-y-auto flex-1">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">Veriler yükleniyor...</p>
              </div>
            ) : (
              <>
                    {/* Desen Seçimi - Sadece hideDesenSelection false ve edit modu değilse göster */}
                    {!hideDesenSelection && !editData && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 sm:p-3 border border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Desen Seçimi</h3>

                  {/* Desen Arama Dropdown */}
                  <div className="relative">
                    <label className="block text-xs sm:text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Desen Ara</label>
                    <input
                      type="text"
                      placeholder="Desen no ile ara..."
                      value={desenSearch}
                      onChange={(e) => {
                        setDesenSearch(e.target.value);
                          setSelectedDesen(null);
                      }}
                      onFocus={() => setShowDropdowns(prev => ({ ...prev, desen: true }))}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      style={{ minHeight: '44px' }}
                    />

                    {showDropdowns.desen && (
                      <div
                        className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl max-h-96 overflow-y-auto"
                        onClick={handleDropdownContainerClick}
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-2 sm:p-3">
                          {desenler
                            .filter(d => !desenSearch || (d.desen_no || '').toLowerCase().includes(desenSearch.toLowerCase()))
                            .map(desen => (
                            <div
                              key={desen.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDesenSelect(desen);
                                setShowDropdowns({});
                                setDesenSearch(desen.desen_no || '');
                              }}
                              className="group flex flex-col items-center p-2 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md cursor-pointer transition-all duration-200"
                              style={{ minHeight: '44px' }}
                            >
                              <div className="w-12 h-16 sm:w-16 sm:h-20 bg-gray-50 rounded flex items-center justify-center mb-1 overflow-hidden">
                                {desen.gorsel_urls?.[0] ? (
                                  <div className="w-full h-full rounded group-hover:scale-105 transition-transform duration-200">
                                    <BaskiGorselComponent
                                      imageUrl={desen.gorsel_urls[0]}
                                      alt={desen.desen_no || ''}
                                      useThumbnail
                                      priority={false}
                                    />
                                  </div>
                                ) : (
                                  <Package className="h-4 w-4 sm:h-6 sm:w-6 text-gray-400" />
                                )}
                              </div>
                              <span className="text-xs font-medium text-gray-900 text-center leading-tight break-words">{desen.desen_no}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Seçili Desen */}
                  {selectedDesen && (
                    <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 overflow-hidden">
                          {getFirstBaskiGorselUrl(selectedDesen.gorsel_urls) ? (
                            <div
                              className="w-full h-full cursor-pointer rounded-lg relative"
                              onClick={() => handleImageClick(selectedDesen.gorsel_urls, selectedDesen.desen_no)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleImageClick(selectedDesen.gorsel_urls, selectedDesen.desen_no);
                                }
                              }}
                            >
                              <BaskiGorselComponent
                                imageUrl={getFirstBaskiGorselUrl(selectedDesen.gorsel_urls)}
                                alt={selectedDesen.desen_no || ''}
                                useThumbnail
                                priority={false}
                              />
                              {normalizeBaskiGorselUrls(selectedDesen.gorsel_urls).length > 1 && (
                                <span className="absolute -bottom-1 -right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-600 text-white">
                                  +{normalizeBaskiGorselUrls(selectedDesen.gorsel_urls).length - 1}
                                </span>
                              )}
                            </div>
                          ) : (
                            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Seçili Desen</h4>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words">{selectedDesen.desen_no}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                    )}


                  {/* Varyantlar */}
                  {selectedDesen && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 sm:p-3 border border-gray-200 dark:border-gray-600">
                    <h3 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Varyantlar</h3>
                    {isParcaBaski && (
                      <p className="text-[11px] text-orange-800 dark:text-orange-200 mb-2">
                        Beden adetlerinin toplamı otomatik olarak iş adedine yazılır
                      </p>
                    )}
                    <div className={`grid gap-2 ${isParcaBaski ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'}`}>
                          {varyantlar.map(varyant => {
                            const selectedVaryant = siparis.varyantlar.find(v => v.desen_varyant_id === varyant.id);
                            const bedenRows = isParcaBaski
                              ? (selectedVaryant ? getVaryantBedenRows(selectedVaryant) : cloneDefaultParcaBaskiBedenRows())
                              : [];
                            const adetToplam = isParcaBaski
                              ? sumBedenAdet(bedenRows)
                              : parseFloat(selectedVaryant?.metre || 0);
                            const metre = isParcaBaski ? adetToplam : (selectedVaryant?.metre || '');
                            const isSelected = isParcaBaski ? adetToplam > 0 : parseFloat(metre) > 0;

                            return (
                              <div
                                key={varyant.id}
                            className={`group relative border-2 rounded overflow-hidden transition-all duration-200 flex flex-col ${
                              isSelected
                                ? 'border-green-400 bg-green-50 shadow-sm'
                                : isParcaBaski
                                  ? 'border-orange-200 dark:border-orange-800/50 bg-white dark:bg-gray-800'
                                  : 'border-gray-200 hover:border-purple-300 hover:shadow-sm'
                            }`}
                          >
                            {/* İçerik: Resim solda, bilgiler sağda */}
                            <div className="flex items-center p-1.5 gap-1.5">
                              {/* Resim */}
                              <div 
                                className="w-12 h-14 bg-gray-50 flex items-center justify-center overflow-hidden cursor-pointer rounded flex-shrink-0 relative"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const urls = normalizeBaskiGorselUrls(
                                    varyant.gorsel_urls?.length
                                      ? varyant.gorsel_urls
                                      : (varyant.desen?.gorsel_urls || selectedDesen?.gorsel_urls || [])
                                  );
                                  if (urls.length > 0) {
                                    handleImageClick(urls, varyant.varyant_adi || selectedDesen?.desen_no || 'Ana');
                                  }
                                }}
                              >
                                  {(() => {
                                    const urls = normalizeBaskiGorselUrls(
                                      varyant.gorsel_urls?.length
                                        ? varyant.gorsel_urls
                                        : (varyant.desen?.gorsel_urls || selectedDesen?.gorsel_urls || [])
                                    );
                                    const thumbUrl = urls[0];
                                    if (!thumbUrl) {
                                      return (
                                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                          <Package className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                                        </div>
                                      );
                                    }
                                    return (
                                      <div className="w-full h-full group-hover:scale-105 transition-transform duration-200 relative">
                                        <BaskiGorselComponent
                                          imageUrl={thumbUrl}
                                          alt={varyant.varyant_adi || 'Varyant'}
                                          useThumbnail
                                          priority={false}
                                        />
                                        {urls.length > 1 && (
                                          <span className="absolute bottom-0 right-0 text-[8px] font-bold px-1 rounded-tl bg-black/70 text-white">
                                            +{urls.length - 1}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>

                              {/* Sağ taraf: varyant kodu ve metre input */}
                              <div className="flex-1 min-w-0">
                                <div className={`text-xs font-medium mb-0.5 break-words ${
                                  isSelected ? 'text-green-800' : 'text-gray-700'
                                  }`}>
                                    {varyant.varyant_adi || 'Ana'}
                                  </div>
                                <div className="flex items-center space-x-1">
                                    <input
                                      data-varyant-id={varyant.id}
                                      type="number"
                                      placeholder="0"
                                      value={metre === 0 && isParcaBaski ? '' : metre}
                                      readOnly={isParcaBaski}
                                      onChange={(e) => {
                                        if (isParcaBaski) return;
                                        e.stopPropagation();
                                        const value = e.target.value;
                                        handleVaryantMetreChange(varyant.id, value === '' ? '' : parseFloat(value) || '');
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className={`w-12 px-1 py-1 text-xs border rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 transition-colors [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] ${
                                        isParcaBaski
                                          ? 'border-orange-200 bg-orange-50/80 text-orange-900 cursor-default'
                                          : isSelected
                                            ? 'border-green-300 bg-green-50 text-green-800'
                                            : 'border-gray-300 bg-white text-gray-900'
                                      }`}
                                      min="0"
                                      step={isParcaBaski ? '1' : '0.1'}
                                    />
                                  <span className="text-xs text-gray-500">{isParcaBaski ? 'adet' : 'm'}</span>
                                </div>
                              </div>
                                  </div>

                            {/* Seçili İşareti */}
                                  {isSelected && (
                              <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full p-0.5 shadow-sm">
                                      <Check className="h-2 w-2" />
                                    </div>
                                  )}

                            {renderVaryantBedenBlock(varyant.id, bedenRows)}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                {/* Sipariş Bilgileri */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 sm:p-3 border border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Sipariş Bilgileri</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {/* 1. Satır: Sipariş Sahibi - Stok Kartı - İşletme */}
                          
                          {/* Sipariş Sahibi */}
                    <div className="relative">
                      <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Müşteri</label>
                      <button
                        onClick={() => toggleDropdown('musteri')}
                        className="w-full text-left px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {(() => {
                          const musteri = musteriler.find(m => String(m.id) === String(siparis.musteri_id));
                          return musteri?.name || siparis.musteri_adi || 'Müşteri seçin';
                        })()}
                      </button>

                      {showDropdowns.musteri && (
                        <div
                          className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                          onClick={handleDropdownContainerClick}
                        >
                          {musteriler.map(musteri => (
                            <div
                              key={musteri.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelect('musteri_id', musteri);
                              }}
                              className={`px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-white ${
                                String(siparis.musteri_id) === String(musteri.id) ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : ''
                              }`}
                            >
                              {musteri.name}
                              {String(siparis.musteri_id) === String(musteri.id) && <Check className="inline ml-2 h-4 w-4" />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                          {/* Stok Kartı */}
                          <div className="relative">
                            <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Stok Kartı</label>
                            <button
                              onClick={() => {
                                toggleDropdown('stokKarti');
                              }}
                              className="w-full text-left px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            >
                              {(() => {
                                // Edit modunda: varyanttan stok kartı ID'sini al
                                const stokKartId = editData && siparis.varyantlar.length > 0 && siparis.varyantlar[0].stok_kart_id
                                  ? siparis.varyantlar[0].stok_kart_id
                                  : siparis.stok_karti_id;
                                return stokKartlari.find(s => s.id === stokKartId)?.name || stokKartlari.find(s => s.id === stokKartId)?.stockName || 'Stok kartı seçin';
                              })()}
                            </button>

                            {showDropdowns.stokKarti && (
                              <div
                                className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                onClick={handleDropdownContainerClick}
                              >
                                {stokKartlari.length > 0 ? stokKartlari.map(stok => (
                                  <div
                                    key={stok.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelect('stok_karti_id', stok);
                                    }}
                                    className={`px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-white ${
                                      (() => {
                                        // Edit modunda: varyanttan stok kartı ID'sini al
                                        const stokKartId = editData && siparis.varyantlar.length > 0 && siparis.varyantlar[0].stok_kart_id
                                          ? siparis.varyantlar[0].stok_kart_id
                                          : siparis.stok_karti_id;
                                        return stokKartId === stok.id;
                                      })() ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : ''
                                    }`}
                            >
                                    {stok.name || stok.stockName}
                                    {(() => {
                                      // Edit modunda: varyanttan stok kartı ID'sini al
                                      const stokKartId = editData && siparis.varyantlar.length > 0 && siparis.varyantlar[0].stok_kart_id
                                        ? siparis.varyantlar[0].stok_kart_id
                                        : siparis.stok_karti_id;
                                      return stokKartId === stok.id;
                                    })() && <Check className="inline ml-2 h-4 w-4" />}
                                  </div>
                                )) : (
                                  <div className="px-3 py-2 text-gray-500 dark:text-gray-400">Stok kartı bulunamadı</div>
                                )}
                              </div>
                            )}
                          </div>

                    {/* İşletme */}
                    <div className="relative">
                      <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">İşletme</label>
                      <button
                        onClick={() => toggleDropdown('isletme')}
                        className="w-full text-left px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {isletmeler.find(i => i.id === siparis.isletme_id)?.name || 'İşletme seçin'}
                      </button>

                      {showDropdowns.isletme && (
                        <div
                          className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                          onClick={handleDropdownContainerClick}
                        >
                          {isletmeler.map(isletme => (
                            <div
                              key={isletme.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                      // seçili işletmeyi ata ve prosesleri yükle
                                      setSiparis(prev => ({ ...prev, isletme_id: isletme.id }));
                                handleIsletmeChange(isletme.id);
                                setShowDropdowns({});
                              }}
                              className={`px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-white ${
                                siparis.isletme_id === isletme.id ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : ''
                              }`}
                            >
                              {isletme.name}
                              {siparis.isletme_id === isletme.id && <Check className="inline ml-2 h-4 w-4" />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                          {/* 2. Satır: Proses - Proses Fiyatı - Para Birimi */}

                    {/* Proses */}
                    <div className="relative">
                      <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Proses</label>
                      <button
                        onClick={() => toggleDropdown('proses')}
                        className="w-full text-left px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {prosesler.find(p => p.id === siparis.proses_id)?.processName || 'Proses seçin'}
                      </button>

                      {showDropdowns.proses && (
                        <div
                          className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                          onClick={handleDropdownContainerClick}
                        >
                          {prosesler.map(proses => (
                            <div
                              key={proses.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                      handleProsesChange(proses);
                                setShowDropdowns({});
                              }}
                              className={`px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-white ${
                                siparis.proses_id === proses.id ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : ''
                              }`}
                            >
                              {proses.processName}
                              {siparis.proses_id === proses.id && <Check className="inline ml-2 h-4 w-4" />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                          {/* Proses Fiyatı */}
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Fiyat</label>
                      <input
                        type="number"
                        value={siparis.proses_fiyati}
                        onChange={(e) => setSiparis(prev => ({ ...prev, proses_fiyati: e.target.value }))}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>

                    {/* Para Birimi */}
                    <div className="relative">
                      <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Para Birimi</label>
                      <button
                        onClick={() => toggleDropdown('doviz')}
                        className="w-full text-left px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {paraBirimleri.find(p => p.id === siparis.doviz_id)?.code || 'Para birimi seçin'}
                      </button>

                      {showDropdowns.doviz && (
                        <div
                          className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                          onClick={handleDropdownContainerClick}
                        >
                          {paraBirimleri.map(doviz => (
                            <div
                              key={doviz.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelect('doviz_id', doviz);
                              }}
                              className={`px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-white ${
                                siparis.doviz_id === doviz.id ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : ''
                              }`}
                            >
                              {doviz.code}
                              {siparis.doviz_id === doviz.id && <Check className="inline ml-2 h-4 w-4" />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Notlar Bölümü */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 sm:p-3 border border-gray-200 dark:border-gray-600">
                      <h3 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Notlar</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {/* Sipariş Notu */}
                        <div>
                          <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                            Sipariş Notu
                          </label>
                          <textarea
                            value={siparis.notlar}
                            onChange={(e) => setSiparis(prev => ({ ...prev, notlar: e.target.value }))}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Sipariş notu..."
                            rows={2}
                          />
                        </div>

                        {/* İşletme Notu */}
                        {siparis.isletme_id && (
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                              İşletme Notu
                            </label>
                            <textarea
                              value={siparis.isletme_notu}
                              onChange={(e) => setSiparis(prev => ({ ...prev, isletme_notu: e.target.value }))}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              placeholder="İşletme notu..."
                              rows={2}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 p-3 border-t border-gray-200 dark:border-gray-600 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-1.5 text-xs text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Kaydediliyor...' : (editData ? 'Güncelle' : 'Kaydet')}
            </button>
          </div>
        </div>
      </div>

      <BaskiGorselGalleryModal
        isOpen={galleryState.open || (showImageModal && !!selectedImage)}
        onClose={handleCloseImageModal}
        title={galleryState.title || selectedImage?.title || 'Desen görselleri'}
        gorselUrls={
          galleryState.urls.length > 0
            ? galleryState.urls
            : (selectedImage?.url ? [selectedImage.url] : [])
        }
        initialIndex={galleryState.index || 0}
        zIndexClass="z-[70]"
      />
    </div>
  );
};

export default SiparisOlusturModal;